import retry from "async-retry";
import { gql } from "@apollo/client";
import buildGraphqlProvider from "ra-data-graphql-simple";
import graphQLClient from "./graphqlClient";

const pageSize = 1000;
const _getListSymbol = Symbol();

const graphqlDataProvider = buildGraphqlProvider({
  client: graphQLClient,
  introspection: {},
});

graphqlDataProvider[_getListSymbol] = graphqlDataProvider.getList;

// gql`
//   query allSubscribers(
//     $page: Int
//     $perPage: Int
//     $sortField: String
//     $sortOrder: String
//     $filter: SubscriberFilter
//   ) {
//     items: allSubscribers(
//       page: $page
//       perPage: $perPage
//       sortField: $sortField
//       sortOrder: $sortOrder
//       filter: $filter
//     ) {
//       id
//       providerId
//       stripeCustomerId
//     }
//     total: _allSubscribersMeta(
//       page: $page
//       perPage: $perPage
//       filter: $filter
//     ) {
//       count
//     }
//   }
// `;

graphqlDataProvider.promoteProviders = async (ids) => {
  return await graphQLClient.mutate({
    mutation: gql`
      mutation promoteProviders($ids: [Int]) {
        promoteProviders(ids: $ids) {
          debug
          matches {
            matchingCriteria {
              customer {
                email
                shortUserId
                firstName
                lastName
              }
              serviceName
              restrictToProviders
              numDogs
              numCats
              children {
                birthdayTimestamp
              }
              specialNeeds
              geoLocation {
                lng
                lng
              }
              enrichmentActivities
            }
            providers {
              shortUserId
              email
              firstName
              lastName
            }
          }
        }
      }
    `,
    variables: { ids },
  });
};

graphqlDataProvider.runReverseMatch = async ({ pastDays, dryRun }) => {
  return await graphQLClient.mutate({
    mutation: gql`
      mutation runReverseMatch($pastDays: Int, $dryRun: Boolean) {
        runReverseMatch(pastDays: $pastDays, dryrun: $dryRun) {
          debug
          matches {
            matchingCriteria {
              customer {
                email
                shortUserId
                firstName
                lastName
              }
              serviceName
              restrictToProviders
              numDogs
              numCats
              children {
                birthdayTimestamp
              }
              specialNeeds
              geoLocation {
                lng
                lng
              }
              enrichmentActivities
            }
            providers {
              shortUserId
              email
              firstName
              lastName
            }
          }
        }
      }
    `,
    variables: { pastDays, dryRun },
  });
};

graphqlDataProvider.generateStatsReport = async () => {
  return await graphQLClient.mutate({
    mutation: gql`
      mutation generateStatsReport($input: GenerateStatsReportInput) {
        generateStatsReport(input: $input) {
          reportId
          periodDays
          startDate
          endDate
          createdAt
          recordCount
          deletedOldRecords
        }
      }
    `,
    variables: {
      input: null,
    },
  });
};

graphqlDataProvider.getLatestStatsReport = async () => {
  return await graphQLClient.query({
    query: gql`
      query latestStatsReport {
        latestStatsReport {
          reports {
            reportId
            periodDays
            startDate
            endDate
            createdAt
            recordCount
          }
          supportedPeriods
        }
      }
    `,
  });
};

// Custom Stats list query to handle periodDays filter properly
const getStatsList = async (params) => {
  const { page, perPage } = params.pagination || { page: 1, perPage: 25 };
  const { field: sortField, order: sortOrder } = params.sort || {
    field: "firstName",
    order: "ASC",
  };
  const filter = params.filter || {};

  // Only pass the fields that are defined in StatsFilter
  const statsFilter = {};
  if (filter.periodDays !== undefined)
    statsFilter.periodDays = Number(filter.periodDays);
  if (filter.startDate !== undefined) statsFilter.startDate = filter.startDate;
  if (filter.endDate !== undefined) statsFilter.endDate = filter.endDate;
  if (filter.isSubscriber !== undefined)
    statsFilter.isSubscriber = filter.isSubscriber;
  if (filter.providerId !== undefined)
    statsFilter.providerId = Number(filter.providerId);

  const result = await graphQLClient.query({
    fetchPolicy: "network-only",
    query: gql`
      query allStats(
        $page: Int
        $perPage: Int
        $sortField: String
        $sortOrder: String
        $filter: StatsFilter
      ) {
        items: allStats(
          page: $page
          perPage: $perPage
          sortField: $sortField
          sortOrder: $sortOrder
          filter: $filter
        ) {
          id
          firstName
          lastName
          email
          recommendations
          bookingsReceived
          bookingsAccepted
          interviewsReceived
          interviewsAccepted
          subscriptionStartDate
          reqRec
          totalApiariSession
          isSubscriber
          zipcode
          state
          operationRadius
          activationDate
          services
          prices
          reliabilityScore
          childcareAgeRangeMin
          childcareAgeRangeMax
          regularSchedule
          adminNotes
        }
        total: _allStatsMeta(page: $page, perPage: $perPage, filter: $filter) {
          count
        }
      }
    `,
    variables: {
      page: page - 1,
      perPage,
      sortField,
      sortOrder: sortOrder.toLowerCase(),
      filter: Object.keys(statsFilter).length > 0 ? statsFilter : null,
    },
  });

  const items = (result.data.items || []).map((item) => ({
    ...item,
    prices: item.prices ? JSON.parse(item.prices) : {},
    regularSchedule: item.regularSchedule
      ? JSON.parse(item.regularSchedule)
      : [],
  }));

  return {
    data: items,
    total: result.data.total?.count || 0,
  };
};

// TODO: remove console.log, revise logic
graphqlDataProvider.getList = (resource, params) => {
  console.log("resource", resource);
  if (resource === "Subscriber") {
    return Promise.resolve({
      data: [],
      total: 10,
    });
  }

  // Use custom handler for Stats to properly filter fields
  if (resource === "Stats") {
    return getStatsList(params);
  }

  if (params?.pagination?.perPage > pageSize) {
    return new Promise(async (resolve, reject) => {
      const bulkResult = [];

      const getPageOfData = async (page) => {
        try {
          const result = await retry(
            async () =>
              graphqlDataProvider[_getListSymbol](resource, {
                ...params,
                pagination: { page, perPage: pageSize },
              }),
            {
              retries: 3,
              onRetry: (err) => {
                console.info(`Retry caused by:`, err);
              },
            },
          );

          bulkResult.push(...result.data);

          if (result.data.length >= pageSize) {
            await getPageOfData(page + 1);
          }
        } catch (err) {
          return reject(err);
        }
      };

      await getPageOfData(1);

      resolve({
        data: bulkResult,
        total: bulkResult.length,
      });
    });
  } else {
    return graphqlDataProvider[_getListSymbol](resource, params);
  }
};

export default graphqlDataProvider;
